import { ConsolidatedClientDetailResponse } from "../entities/consolidated/ConsolidatedClientDetailResponse";
import {
  ConsolidatedClientListRequest,
  ConsolidatedClientListResponse
} from "../entities/consolidated/ConsolidatedClientListResponse";
import { ConsolidatedDashboardResponse } from "../entities/consolidated/ConsolidatedDashboardResponse";
import {
  ConsolidatedIoListRequest,
  ConsolidatedIoListResponse
} from "../entities/consolidated/ConsolidatedIoListResponse";
import {
  ConsolidatedGetSettingsResponse,
  ConsolidatedSetSettingsRequest
} from "../entities/consolidated/ConsolidatedSettings";
import { ClientListRequest } from "../pages/client/entities/ClientListRequest";
import { ClientGetResponse } from "../pages/client/entities/ClientGetResponse";
import { ClientListResponse } from "../pages/client/entities/ClientListResponse";
import { InvoiceListRequest } from "../pages/client/invoice/entities/InvoiceListRequest";
import { InvoiceListResponse } from "../pages/client/invoice/entities/InvoiceListResponse";
import { InvoiceGetResponse } from "../pages/client/invoice/entities/InvoiceGetResponse";
import { InvoiceFields } from "./InvoiceService";
import { CurrencyTotal } from "../entities/shared/CurrencyTotal";
import { ReportActivitySummaryRequest } from "../entities/reports/ReportActivitySummary/ReportActivitySummaryRequest";
import { ReportActivitySummaryResponse } from "../entities/reports/ReportActivitySummary/ReportActivitySummaryResponse";
import CompanyService from "./CompanyService";
import ExportService from "./ExportService";
import RequestService from "./RequestService";

class ConsolidatedService {
  public static getSettings(): Promise<
    Error | ConsolidatedGetSettingsResponse
  > {
    return RequestService.post<ConsolidatedGetSettingsResponse>(
      "/consolidated/getsettings",
      {}
    );
  }

  public static setSettings(
    request: ConsolidatedSetSettingsRequest
  ): Promise<Error | unknown> {
    return RequestService.post("/consolidated/setsettings", request);
  }

  public static getDashboard(): Promise<ConsolidatedDashboardResponse | Error> {
    return RequestService.post<ConsolidatedDashboardResponse>(
      "/consolidated/getdashboard",
      {}
    );
  }

  /**
   * Obtiene el listado de clientes consolidados
   * Usa el endpoint dedicado de consolidado y adapta la respuesta al modelo de tabla actual.
   */
  public static getConsolidatedClientsList(
    request: ClientListRequest
  ): Promise<ClientListResponse | Error> {
    const sortColumnMap: Record<string, string> = {
      name: "name",
      cant: "qty",
      amount: "amount",
      due: "dueAmount"
    };

    const endpointRequest: ConsolidatedClientListRequest = {
      filter: request.filter ?? "",
      page: request.page,
      pageSize: request.pagesize,
      sortColumn:
        sortColumnMap[request.sortColumn] || request.sortColumn || "amount",
      descending: String(request.descending ?? 1),
      text: request.text ?? ""
    };

    return RequestService.post<ConsolidatedClientListResponse>(
      "/consolidated/getclientlist",
      endpointRequest
    ).then((result) => {
      if (result instanceof Error) {
        return result;
      }

      const response = result as ConsolidatedClientListResponse;
      return {
        iocount: response.CountTotal ?? 0,
        total: response.Total ?? 0,
        max: response.MaxDebt ?? 0,
        page: response.Page ?? request.page ?? 0,
        pageSize: response.PageSize ?? request.pagesize ?? 50,
        itemCount: response.CountClient ?? 0,
        list: (response.Clients ?? []).map((client) => ({
          ...client,
          additionals: Array.isArray(client.PersonAdditional)
            ? client.PersonAdditional
            : [],
          Person_Segment: Array.isArray(client.Person_Segment)
            ? client.Person_Segment
            : []
        })),
        response: "Ok",
        Id: 0
      } as ClientListResponse;
    });
  }

  /**
   * Obtiene el detalle de cliente para consolidado usando personKey.
   */
  public static getConsolidatedClientDetailRaw(
    pk: string
  ): Promise<ConsolidatedClientDetailResponse | Error> {
    return RequestService.post<ConsolidatedClientDetailResponse>(
      "/consolidated/getclientdetail",
      { pk }
    );
  }

  public static exportClients(request: ClientListRequest, count: number) {
    const sortColumnMap: Record<string, string> = {
      name: "name",
      cant: "qty",
      amount: "amount",
      due: "dueAmount"
    };
    const endpointRequest: ConsolidatedClientListRequest = {
      filter: request.filter ?? "",
      page: request.page,
      pageSize: request.pagesize,
      sortColumn:
        sortColumnMap[request.sortColumn] || request.sortColumn || "amount",
      descending: String(request.descending ?? 1),
      text: request.text ?? ""
    };
    return ExportService.requestExport(
      "/consolidated/exportclientlist",
      {
        ...endpointRequest,
        additionaldefinitions: CompanyService.getAdditionalDefinitions()
      },
      count,
      "consolidated_clients.xlsx"
    );
  }

  /**
   * Obtiene el detalle de cliente para consolidado usando personKey.
   * Mantiene compatibilidad con el flujo actual devolviendo un ClientGetResponse.
   */
  public static getConsolidatedClientDetail(
    pk: string
  ): Promise<ClientGetResponse | Error> {
    return ConsolidatedService.getConsolidatedClientDetailRaw(pk).then(
      (result) => {
        if (result instanceof Error) {
          return result;
        }
        const response = result as ConsolidatedClientDetailResponse;
        const client = response.Client;
        return {
          response: "Ok",
          item: {
            ...client,
            Name: client?.Name ?? client?.personKey ?? "",
            additionals: Array.isArray(client?.PersonAdditional)
              ? client.PersonAdditional
              : [],
            Person_Segment: Array.isArray(client?.Person_Segment)
              ? client.Person_Segment
              : []
          },
          id: client?.PersonId ?? 0,
          pendingReceivables: 0,
          segments: [],
          customertoken: client?.Token ?? ""
        } as ClientGetResponse;
      }
    );
  }

  public static getIoDetails(
    ioid: number
  ): Promise<InvoiceGetResponse | Error> {
    return RequestService.get<{
      IO: InvoiceGetResponse.Item;
      IOStatuses: any[];
      IOFiles: any;
      User: any;
    }>(`/consolidated/getiodetails/${ioid}`, {}).then((result) => {
      if (result instanceof Error) return result;
      const io = result.IO;
      if (!io) return new Error("Invoice not found");
      return {
        response: "Ok",
        item: {
          ...io,
          additionals: io.additionals ?? [],
          files: io.files ?? [],
          Transactions: io.Transactions ?? [],
          Tag_IO: io.Tag_IO ?? []
        },
        Id: io.IOID,
        statusLogsHistory: []
      } as InvoiceGetResponse;
    });
  }

  /**
   * Obtiene facturas del detalle consolidado de cliente usando personKey.
   */
  public static getConsolidatedIoList(
    request: InvoiceListRequest,
    pk: string
  ): Promise<InvoiceListResponse | Error> {
    const mappedFilter = request.filter
      ? ConsolidatedService.mapInvoiceFiltersForConsolidated(request.filter)
      : "";
    const endpointRequest: ConsolidatedIoListRequest = {
      filter: mappedFilter,
      page: request.page ?? 0,
      pageSize: request.pagesize ?? 10,
      type: Number(request.quickfilter ?? 1),
      p: Number(request.groupid ?? 0),
      pk,
      sortColumn: request.sortcolumn ?? "",
      descending: String(request.descending ?? 0),
      text: request.text ?? ""
    };

    return RequestService.post<ConsolidatedIoListResponse>(
      "/consolidated/getiolist",
      endpointRequest
    ).then((result) => {
      if (result instanceof Error) {
        return result;
      }
      const response = result as ConsolidatedIoListResponse;
      const list = (response.Facturas ?? []).map((invoice) => ({
        ...invoice,
        additionals: invoice.additionals ?? [],
        files: invoice.files ?? [],
        Transactions: invoice.Transactions ?? [],
        Tag_IO: invoice.Tag_IO ?? []
      }));

      const requestedPageSize = request.pagesize ?? 10;
      const defaultCurrencyId = CompanyService.getDefaultCurrencyId();
      const currencyTotals: CurrencyTotal[][] = [
        [
          {
            id: InvoiceFields.amount,
            value: response.TotalAmount ?? 0,
            currencyid: defaultCurrencyId
          },
          {
            id: InvoiceFields.pending,
            value: response.TotalPending ?? 0,
            currencyid: defaultCurrencyId
          }
        ]
      ];

      return {
        response: "Ok",
        list,
        itemCount: response.Count ?? 0,
        page: response.Page ?? request.page ?? 0,
        pageSize: requestedPageSize,
        total: response.TotalPending ?? 0,
        paid: (response.TotalAmount ?? 0) - (response.TotalPending ?? 0),
        currencyTotals,
        currencyId: defaultCurrencyId
      } as InvoiceListResponse;
    });
  }

  /**
   * El endpoint consolidado de facturas espera filtros "selFields" por nombre (no por IDs V1),
   * y en el caso Company requiere selCompany.
   *
   * Este método convierte el string outer `filter0=...&filter1=...` manteniendo el formato
   * que genera `FilterService.GetExtraFiltersRequestString`.
   */
  private static mapInvoiceFiltersForConsolidated(filter: string): string {
    if (!filter) {
      return "";
    }
    const parts = filter.split("&").filter(Boolean);
    const mappedParts = parts.map((part) => {
      const [k, v] = part.split("=");
      if (!k?.startsWith("filter") || v === undefined) {
        return part;
      }
      const inner = decodeURIComponent(v);
      const params = new URLSearchParams(inner);
      const selFields = params.get("selFields");
      if (!selFields) {
        return part;
      }

      // Map IDs (V1) -> nombre requerido por consolidado
      const mapping: Record<string, string> = {
        "-1023": "EmittedDate",
        "-1024": "DueDate",
        "-1006": "Company",
        "-1009": "ReferenceNumber",
        "-1011": "Amount",
        "-1012": "PendingAmount"
      };
      const mappedField = mapping[selFields] ?? selFields;
      params.set("selFields", mappedField);

      // Company requiere selCompany (en la UI suele venir como personidFilter)
      if (mappedField.toLowerCase() === "company") {
        const personId = params.get("personidFilter");
        if (personId) {
          params.set("selCompany", personId);
        }
      }

      const rebuiltInner = params.toString();
      return `${k}=${encodeURIComponent(rebuiltInner)}`;
    });
    return mappedParts.join("&");
  }

  /**
   * Obtiene el resumen consolidado de gestiones (actividades) por usuario.
   * Usa el nuevo endpoint /consolidated/activitysummary que agrega datos de todas las empresas.
   */
  public static getActivitySummary(
    request: ReportActivitySummaryRequest
  ): Promise<ReportActivitySummaryResponse | Error> {
    const filters = ConsolidatedService.parseFilterStringToKeyValueArray(
      request.filter ?? ""
    );
    const period = ConsolidatedService.extractPeriodFromFilters(filters);

    return RequestService.post<{
      summaryData: ReportActivitySummaryResponse.Item[];
      totalClient: number;
      currentDateFilter: string;
    }>("/consolidated/activitysummary", {
      Period: period,
      Filters: filters,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    }).then((result) => {
      if (result instanceof Error) {
        return result;
      }
      return {
        response: "Ok",
        list: result.summaryData ?? [],
        totalClient: result.totalClient ?? 0,
        date: result.currentDateFilter ?? period
      } as ReportActivitySummaryResponse;
    });
  }

  /**
   * Parsea el string externo "filter0=...&filter1=..." en el array de { Key, Value }
   * que espera el endpoint consolidado, transformando el formato V2 al V1.
   *
   * El string externo tiene cada valor url-encoded, por lo que primero se decodifica
   * el wrapper y luego se convierte el inner query string al formato V1.
   */
  private static parseFilterStringToKeyValueArray(
    filter: string
  ): Array<{ Key: string; Value: string }> {
    if (!filter) return [];

    // El formato es: filter0=<encoded>&filter1=<encoded>
    // Los valores están url-encoded para que el & interno no rompa el split externo
    const result: Array<{ Key: string; Value: string }> = [];
    const parts = filter.split("&");

    for (const part of parts) {
      const eqIdx = part.indexOf("=");
      if (eqIdx < 0) continue;
      const key = part.substring(0, eqIdx);
      if (!key.match(/^filter\d+$/)) continue;

      let innerValue = part.substring(eqIdx + 1);
      try {
        innerValue = decodeURIComponent(innerValue);
      } catch {
        /* usar tal cual */
      }

      result.push({
        Key: key,
        Value: ConsolidatedService.transformFilterToV1Format(innerValue)
      });
    }

    return result;
  }

  /**
   * Convierte el inner query string del formato V2 (generado por filterToString)
   * al formato V1 que espera el endpoint /consolidated/activitysummary.
   *
   * Transformaciones:
   *   - selDate          → dateFilterActivity  (con conversión de formato YYYYMMDD→DD/MM/YYYY)
   *   - dateFilterActivity ya presente → normaliza el formato de fecha igualmente
   *
   * Nota: los valores internos pueden tener "+" como espacio (form-encoding) y valores
   * percent-encoded (%2F, etc.) que se decodifican en esta etapa.
   */
  private static transformFilterToV1Format(innerFilter: string): string {
    const paramMap: Record<string, string> = {};

    for (const part of innerFilter.split("&")) {
      const eqIdx = part.indexOf("=");
      if (eqIdx < 0) continue;
      const k = part.substring(0, eqIdx);
      const rawV = part.substring(eqIdx + 1);
      // "+" es espacio en form-encoding; decodeURIComponent solo no lo convierte
      const decodedV = (() => {
        try {
          return decodeURIComponent(rawV.replace(/\+/g, " "));
        } catch {
          return rawV.replace(/\+/g, " ");
        }
      })();
      paramMap[k] = decodedV;
    }

    // V2 → V1: renombrar selDate → dateFilterActivity
    if ("selDate" in paramMap) {
      paramMap["dateFilterActivity"] =
        ConsolidatedService.normalizeDateRangeToV1(paramMap["selDate"]);
      delete paramMap["selDate"];
    } else if ("dateFilterActivity" in paramMap) {
      // También normalizamos si ya llega con el nombre correcto (puede traer dígitos sueltos o formato incorrecto)
      paramMap["dateFilterActivity"] =
        ConsolidatedService.normalizeDateRangeToV1(
          paramMap["dateFilterActivity"]
        );
    }

    // Defaults V1: el backend los espera en todos los filtros independientemente del tipo
    if (!("operatorContains" in paramMap)) paramMap["operatorContains"] = "3";
    if (!("selOperatorDate" in paramMap)) paramMap["selOperatorDate"] = "0";
    if (!("selOperator" in paramMap)) paramMap["selOperator"] = "0";
    if (!("selStatus" in paramMap)) paramMap["selStatus"] = "0";
    if (!("selYesNo" in paramMap)) paramMap["selYesNo"] = "0";
    if (!("activitytypeFilter" in paramMap))
      paramMap["activitytypeFilter"] = "null";

    return Object.entries(paramMap)
      .map(([k, v]) => `${k}=${v}`)
      .join("&");
  }

  /**
   * Normaliza un rango de fechas a formato "DD/MM/YYYY - DD/MM/YYYY".
   * Acepta:
   *   - "YYYYMMDD-YYYYMMDD"       (generado por formatIntizaDate, sin espacios)
   *   - "DD/MM/YYYY-DD/MM/YYYY"   (seleccionado por el usuario, sin espacios)
   *   - "D/M/YYYY - D/M/YYYY"     (dígitos sueltos, con o sin espacios)
   *   - "DD/MM/YYYY - DD/MM/YYYY" (ya en formato V1 completo)
   */
  private static normalizeDateRangeToV1(dateRange: string): string {
    const trimmed = dateRange.trim();

    // Separar por " - " (con espacios) si existe, para no confundir con guiones de fecha
    if (trimmed.includes(" - ")) {
      const sepIdx = trimmed.indexOf(" - ");
      const start = ConsolidatedService.parseDateToV1(
        trimmed.substring(0, sepIdx).trim()
      );
      const end = ConsolidatedService.parseDateToV1(
        trimmed.substring(sepIdx + 3).trim()
      );
      if (start && end) return `${start} - ${end}`;
      return trimmed;
    }

    // Separar por "-" para "YYYYMMDD-YYYYMMDD" o "DD/MM/YYYY-DD/MM/YYYY"
    const dashIdx = trimmed.indexOf(
      "-",
      trimmed.includes("/") ? trimmed.indexOf("/") + 1 : 0
    );
    if (dashIdx < 0) return trimmed;

    const start = ConsolidatedService.parseDateToV1(
      trimmed.substring(0, dashIdx).trim()
    );
    const end = ConsolidatedService.parseDateToV1(
      trimmed.substring(dashIdx + 1).trim()
    );

    if (!start || !end) return trimmed;
    return `${start} - ${end}`;
  }

  /**
   * Convierte una fecha a "DD/MM/YYYY" (con cero-padding).
   * Acepta: "YYYYMMDD", "DD/MM/YYYY", "D/M/YYYY".
   */
  private static parseDateToV1(dateStr: string): string | null {
    const s = dateStr.trim();

    // Formato YYYYMMDD (8 dígitos seguidos)
    if (/^\d{8}$/.test(s)) {
      return `${s.substring(6, 8)}/${s.substring(4, 6)}/${s.substring(0, 4)}`;
    }

    // Formato D/M/YYYY o DD/MM/YYYY (con dígitos sueltos o dobles)
    const match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      return `${match[1].padStart(2, "0")}/${match[2].padStart(2, "0")}/${match[3]}`;
    }

    return null;
  }

  private static extractPeriodFromFilters(
    filters: Array<{ Key: string; Value: string }>
  ): string {
    for (const f of filters) {
      for (const part of f.Value.split("&")) {
        const eqIdx = part.indexOf("=");
        if (eqIdx < 0) continue;
        if (part.substring(0, eqIdx) === "dateFilterActivity") {
          return part.substring(eqIdx + 1);
        }
      }
    }
    return "";
  }
}

export default ConsolidatedService;
